// Phase A — Ration Allowance Rules admin.
//
// Defines weekly cash-advance rules: how much, who's eligible, when
// paid, attendance conditions. Phase D will use these rules at month-
// end payroll to compute advance recovery per employee.

import { useState } from 'react'
import {
  App, Button, Card, DatePicker, Form, Input, InputNumber, Modal, Popconfirm,
  Select, Space, Switch, Table, Tag, Tooltip, Typography,
} from 'antd'
import {
  DeleteOutlined, EditOutlined, PlusOutlined, ShoppingCartOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs, { Dayjs } from 'dayjs'

import {
  welfareMastersApi, type RationRule,
} from '@/features/hr/welfareMastersApi'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'

const { Text } = Typography


// Map week_start_day 0..6 → day name (Python convention 0=Mon, 6=Sun)
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']


export default function RationRulesPage() {
  return (
    <div>
      <PageHeader
        title="Ration Allowance Rules"
        subtitle="Weekly cash advance for self-food workers. Attendance-conditional. Recovered from month-end salary (Phase D)."
      />
      <RulesCard />
    </div>
  )
}


function RulesCard() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const canEdit = usePermission('employee.create')
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<RationRule | null>(null)

  const { data: rules, isLoading } = useQuery({
    queryKey: ['ration-rules'],
    queryFn: () => welfareMastersApi.listRationRules(),
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['ration-rules'] })

  const createMut = useMutation({
    mutationFn: welfareMastersApi.createRationRule,
    onSuccess: () => { message.success('Rule added'); invalidate(); setCreateOpen(false) },
    onError: errToast(message),
  })
  const patchMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      welfareMastersApi.patchRationRule(id, body),
    onSuccess: () => { message.success('Saved'); invalidate(); setEditing(null) },
    onError: errToast(message),
  })
  const delMut = useMutation({
    mutationFn: welfareMastersApi.deleteRationRule,
    onSuccess: () => { message.success('Deleted'); invalidate() },
    onError: errToast(message),
  })

  return (
    <Card
      style={{ marginTop: 16 }}
      title={<Space><ShoppingCartOutlined /> Ration Rules</Space>}
      extra={canEdit && (
        <Button type="primary" icon={<PlusOutlined />}
          onClick={() => setCreateOpen(true)}>
          Add Rule
        </Button>
      )}
    >
      <Table
        rowKey="id"
        size="small"
        loading={isLoading}
        pagination={{ pageSize: 25 }}
        columns={[
          {
            title: 'Code', dataIndex: 'rule_code', key: 'rc', width: 180,
            render: (s: string, r: RationRule) => (
              <Space size={4} direction="vertical">
                <Text code style={{ fontSize: 11 }}>{s}</Text>
                {r.rule_name && (
                  <Text type="secondary" style={{ fontSize: 11 }}>{r.rule_name}</Text>
                )}
              </Space>
            ),
          },
          {
            title: 'Scope', key: 'scope', width: 240,
            render: (_: unknown, r: RationRule) => (
              <Space size={4} wrap>
                <Tag color="blue">{r.employee_category ?? 'ANY CAT'}</Tag>
                {r.employment_type && <Tag>{r.employment_type}</Tag>}
                {r.accommodation_type && <Tag color="cyan">{r.accommodation_type}</Tag>}
              </Space>
            ),
          },
          {
            title: 'Amount', key: 'amt',
            render: (_: unknown, r: RationRule) => {
              if (r.weekly_amount) return (
                <Space size={4} direction="vertical">
                  <Text strong>₹{Number(r.weekly_amount).toLocaleString('en-IN')}</Text>
                  <Text type="secondary" style={{ fontSize: 11 }}>per week</Text>
                </Space>
              )
              if (r.monthly_amount) return (
                <Space size={4} direction="vertical">
                  <Text strong>₹{Number(r.monthly_amount).toLocaleString('en-IN')}</Text>
                  <Text type="secondary" style={{ fontSize: 11 }}>per month</Text>
                </Space>
              )
              if (r.per_day_amount) return (
                <Space size={4} direction="vertical">
                  <Text strong>₹{Number(r.per_day_amount).toLocaleString('en-IN')}</Text>
                  <Text type="secondary" style={{ fontSize: 11 }}>per day</Text>
                </Space>
              )
              return <Text type="secondary">—</Text>
            },
          },
          {
            title: 'Frequency', dataIndex: 'payment_frequency', key: 'freq', width: 110,
            render: (v: string, r: RationRule) => (
              <Space size={4} direction="vertical">
                <Tag color="purple">{v}</Tag>
                {v === 'WEEKLY' && (
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    starts {DAY_NAMES[r.week_start_day] ?? '?'}
                  </Text>
                )}
              </Space>
            ),
          },
          {
            title: 'Attendance rule', key: 'att',
            render: (_: unknown, r: RationRule) => (
              <Space size={4} direction="vertical">
                {r.attendance_required ? (
                  <Tag color="orange">Attendance required</Tag>
                ) : (
                  <Tag>Not required</Tag>
                )}
                {r.minimum_present_days_per_week && (
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    min {r.minimum_present_days_per_week} days/wk
                  </Text>
                )}
                <Space size={4}>
                  <Tooltip title="Pays even on approved leave">
                    <Tag color={r.payable_if_on_leave ? 'green' : 'default'}>
                      Leave: {r.payable_if_on_leave ? '✓' : '✗'}
                    </Tag>
                  </Tooltip>
                  <Tooltip title="Pays even when absent">
                    <Tag color={r.payable_if_absent ? 'green' : 'default'}>
                      Absent: {r.payable_if_absent ? '✓' : '✗'}
                    </Tag>
                  </Tooltip>
                </Space>
              </Space>
            ),
          },
          {
            title: 'Mode', dataIndex: 'payment_mode', key: 'mode', width: 130,
            render: (v: string) => <Tag>{v.replace('_', ' ')}</Tag>,
          },
          {
            title: 'Active', dataIndex: 'is_active', key: 'a', width: 80,
            render: (b: boolean) => (
              <Tag color={b ? 'success' : 'default'}>{b ? 'YES' : 'NO'}</Tag>
            ),
          },
          ...(canEdit ? [{
            title: '', key: 'act', width: 80,
            render: (_: unknown, r: RationRule) => (
              <Space size={0}>
                <Button size="small" type="text" icon={<EditOutlined />}
                  onClick={() => setEditing(r)} />
                <Popconfirm title="Delete this rule?"
                  onConfirm={() => delMut.mutate(r.id)}>
                  <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          }] : []),
        ] as never}
        dataSource={rules ?? []}
      />
      <RationRuleFormModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={createMut.mutate}
        submitting={createMut.isPending}
      />
      <RationRuleFormModal
        open={!!editing}
        rule={editing}
        onClose={() => setEditing(null)}
        onSubmit={(body) => editing && patchMut.mutate({ id: editing.id, body })}
        submitting={patchMut.isPending}
      />
    </Card>
  )
}


function RationRuleFormModal({
  open, rule, onClose, onSubmit, submitting,
}: {
  open: boolean
  rule?: RationRule | null
  onClose: () => void
  onSubmit: (body: Record<string, unknown>) => void
  submitting: boolean
}) {
  const [form] = Form.useForm()
  const isEdit = !!rule

  return (
    <Modal
      open={open} onCancel={onClose} destroyOnClose
      title={isEdit ? `Edit ${rule?.rule_code}` : 'Add Ration Rule'}
      okText={isEdit ? 'Save' : 'Add'} confirmLoading={submitting} width={620}
      onOk={() => form.validateFields().then((v) => {
        // Sanity: must specify at least one amount
        if (!v.weekly_amount && !v.monthly_amount && !v.per_day_amount) {
          form.setFields([{ name: 'weekly_amount',
            errors: ['Specify weekly, monthly, or per-day amount'] }])
          return
        }
        const body: Record<string, unknown> = {
          ...v,
          effective_from: (v.effective_from as Dayjs).format('YYYY-MM-DD'),
          effective_to: v.effective_to ? (v.effective_to as Dayjs).format('YYYY-MM-DD') : null,
          weekly_amount: v.weekly_amount?.toString() ?? null,
          monthly_amount: v.monthly_amount?.toString() ?? null,
          per_day_amount: v.per_day_amount?.toString() ?? null,
        }
        onSubmit(body)
      })}
    >
      <Form form={form} layout="vertical" initialValues={rule ? {
        ...rule,
        effective_from: dayjs(rule.effective_from),
        effective_to: rule.effective_to ? dayjs(rule.effective_to) : null,
        weekly_amount: rule.weekly_amount ? Number(rule.weekly_amount) : null,
        monthly_amount: rule.monthly_amount ? Number(rule.monthly_amount) : null,
        per_day_amount: rule.per_day_amount ? Number(rule.per_day_amount) : null,
      } : {
        attendance_required: true,
        payable_if_on_leave: false,
        payable_if_absent: false,
        payment_frequency: 'WEEKLY',
        payment_mode: 'PAYROLL_ALLOWANCE',
        week_start_day: 5,
        effective_from: dayjs(),
      }}>
        <Space.Compact block>
          <Form.Item name="rule_code" label="Rule Code"
            rules={[{ required: true, min: 2 }]} style={{ flex: 1, marginRight: 8 }}>
            <Input placeholder="WORKER_SELF_FOOD_WEEKLY" disabled={isEdit} />
          </Form.Item>
          <Form.Item name="rule_name" label="Display Name" style={{ flex: 1 }}>
            <Input placeholder="Worker self-food weekly" />
          </Form.Item>
        </Space.Compact>
        <Space.Compact block>
          <Form.Item name="employee_category" label="Employee Category"
            style={{ flex: 1, marginRight: 8 }}>
            <Select allowClear placeholder="any" options={[
              { label: 'Worker', value: 'WORKER' },
              { label: 'Staff', value: 'STAFF' },
              { label: 'Contract', value: 'CONTRACT' },
              { label: 'Trainee', value: 'TRAINEE' },
            ]} />
          </Form.Item>
          <Form.Item name="employment_type" label="Employment Type"
            style={{ flex: 1, marginRight: 8 }}>
            <Select allowClear placeholder="any" options={[
              { label: 'Permanent', value: 'PERMANENT' },
              { label: 'Contract', value: 'CONTRACT' },
              { label: 'Trainee', value: 'TRAINEE' },
            ]} />
          </Form.Item>
          <Form.Item name="accommodation_type" label="Accommodation"
            style={{ flex: 1 }}>
            <Select allowClear placeholder="any" options={[
              { label: 'Company Colony', value: 'COMPANY_COLONY' },
              { label: 'Labour Room', value: 'LABOUR_ROOM' },
              { label: 'Hostel', value: 'HOSTEL' },
              { label: 'Staff Quarter', value: 'STAFF_QUARTER' },
              { label: 'Rented by company', value: 'RENTED_BY_COMPANY' },
              { label: 'N/A', value: 'NOT_APPLICABLE' },
            ]} />
          </Form.Item>
        </Space.Compact>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          Amount (specify at least one):
        </Text>
        <Space.Compact block>
          <Form.Item name="weekly_amount" label="Weekly ₹"
            style={{ flex: 1, marginRight: 8 }}>
            <InputNumber min={0} step={50} style={{ width: '100%' }}
              placeholder="e.g. 500" />
          </Form.Item>
          <Form.Item name="monthly_amount" label="Monthly ₹"
            style={{ flex: 1, marginRight: 8 }}>
            <InputNumber min={0} step={50} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="per_day_amount" label="Per-day ₹"
            style={{ flex: 1 }}>
            <InputNumber min={0} step={10} style={{ width: '100%' }} />
          </Form.Item>
        </Space.Compact>
        <Space.Compact block>
          <Form.Item name="payment_frequency" label="Frequency"
            style={{ flex: 1, marginRight: 8 }}>
            <Select options={[
              { label: 'Weekly', value: 'WEEKLY' },
              { label: 'Monthly', value: 'MONTHLY' },
              { label: 'Attendance-based', value: 'ATTENDANCE_BASED' },
            ]} />
          </Form.Item>
          <Form.Item name="week_start_day" label="Week starts on"
            style={{ flex: 1, marginRight: 8 }}
            tooltip="Day of week that the ration cycle starts. Indian mills usually pay Saturday.">
            <Select options={DAY_NAMES.map((d, i) => ({ label: d, value: i }))} />
          </Form.Item>
          <Form.Item name="payment_mode" label="Payment Mode"
            style={{ flex: 1 }}>
            <Select options={[
              { label: 'Cash', value: 'CASH' },
              { label: 'Payroll Allowance', value: 'PAYROLL_ALLOWANCE' },
              { label: 'Voucher', value: 'VOUCHER' },
              { label: 'Store Issue', value: 'STORE_ISSUE' },
              { label: 'Digital Wallet', value: 'DIGITAL_WALLET' },
            ]} />
          </Form.Item>
        </Space.Compact>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          Attendance rules:
        </Text>
        <Space.Compact block>
          <Form.Item name="attendance_required" label="Attendance required?"
            valuePropName="checked" style={{ flex: 1, marginRight: 8 }}>
            <Switch />
          </Form.Item>
          <Form.Item name="minimum_present_days_per_week" label="Min days/week"
            style={{ flex: 1, marginRight: 8 }}>
            <InputNumber min={0} max={7} style={{ width: '100%' }}
              placeholder="e.g. 5" />
          </Form.Item>
          <Form.Item name="payable_if_on_leave" label="Pay on leave?"
            valuePropName="checked" style={{ flex: 1, marginRight: 8 }}>
            <Switch />
          </Form.Item>
          <Form.Item name="payable_if_absent" label="Pay if absent?"
            valuePropName="checked" style={{ flex: 1 }}>
            <Switch />
          </Form.Item>
        </Space.Compact>
        <Space.Compact block>
          <Form.Item name="effective_from" label="Effective from"
            rules={[{ required: true }]} style={{ flex: 1, marginRight: 8 }}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="effective_to" label="Effective to (optional)"
            style={{ flex: 1 }}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Space.Compact>
        {isEdit && (
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        )}
        <Form.Item name="remarks" label="Remarks">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  )
}


// ─── Helpers ──────────────────────────────────────────────────────────

function errToast(message: ReturnType<typeof App.useApp>['message']) {
  return (e: unknown) => {
    const err = e as { response?: { data?: { detail?: string } } }
    message.error(err?.response?.data?.detail ?? 'Save failed')
  }
}
